import { useState, useEffect, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import Swal from "sweetalert2";
import { debounce } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Loader from "../../../components/Loader/Loader";
import { Link } from "react-router-dom";
import "../../../components/common/css/datatable.scss";
import { Button } from "@mui/material";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import useResponsiveColumns from "../../../hooks/useResponsiveColumns";

// Responsive column visibility (omitted fields stay visible).
const INVESTMENT_COL_PRIORITY = {
  id: "low",
  user_id: "low",
  currency: "low",
  "campaign_detail.corporation_legal_name": "medium",
  paymentOption: "medium",
  suitabilityStatus: "medium",
};

const InvestmentList = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState({
    data: [],
    total: 0,
    page: 1,
    pageSize: 9,
  });
  const fetchInvestData = () => {
    const token = localStorage.getItem("accessToken");
    setLoading(true);
    setPageState((old) => ({ ...old }));
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "admin/get-campaign-invest-listing",
      headers: {
        "x-access-token": token,
      },
      data: {
        page: pageState.page,
        limit: pageState.pageSize,
        searchTerm: query,
      },
    };

    axios(config)
      .then(function (response) {
        setPageState((old) => ({
          ...old,
          data: response?.data?.data?.rows,
          total: response?.data?.data?.count,
        }));
        setLoading(false);
      })
      .catch(function (error) {
        setLoading(false);
        Swal.fire({
          title: error?.response?.data?.message || "An error occurred",
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  };

  useEffect(() => {
    fetchInvestData();
  }, [pageState.page, pageState.pageSize, query]);

  const handleQuery = useCallback(
    debounce((e) => {
      setQuery(e.target.value);
      setPageState({ ...pageState, page: 1 });
    }, 1000)
  );

  const columnVisibilityModel = useResponsiveColumns(INVESTMENT_COL_PRIORITY);

  const userColumns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "user_id",
      headerName: "User ID",
      flex: 1,
      minWidth: 70,
    },

    {
      field: "user.firstname",
      headerName: "Name",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {params.row.user.firstname + " " + params.row.user.lastname}
          </div>
        );
      },
    },

    {
      field: "campaign_detail.campaign_name",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {params?.row?.campaign_detail?.campaign_name}
          </div>
        );
      },
    },
    {
      field: "campaign_detail.corporation_legal_name",
      headerName: "Corporate Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {params?.row?.campaign_detail?.corporation_legal_name}
          </div>
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "currency",
      headerName: "Currency",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "paymentOption",
      headerName: "Payment Method",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "suitabilityStatus",
      headerName: "Suitability Status",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const { isSuitabilityRejected, is_suitability_accepted } = params.row;
        let status = "PENDING";
        let statusColor = "#ff9800"; // Orange for pending
        
        if (isSuitabilityRejected === 1) {
          status = "REJECTED";
          statusColor = "#f44336"; // Red for rejected
        } else if (is_suitability_accepted === 1) {
          status = "APPROVED";
          statusColor = "#4caf50"; // Green for approved
        }
        
        return (
          <div className="rowitem">
            <span 
              style={{ 
                color: statusColor, 
                fontWeight: "bold",
                padding: "4px 8px",
                borderRadius: "4px",
                backgroundColor: statusColor + "20",
                fontSize: "12px"
              }}
            >
              {status}
            </span>
          </div>
        );
      },
    },
    {
      field: "user.profilePic",
      headerName: "Datetime",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const d = new Date(params?.row?.createdAt);
        return (
          <div className="cellWrapper ">
            <div className="profilePic">
              {d?.getHours() + ":" + d?.getMinutes() + ", " + d?.toDateString()}
            </div>
          </div>
        );
      },
    },
  ];

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return (
          <>
            <div className="cellAction">
              <Link
                to={`/investment/${params.row.id} `}
                style={{ textDecoration: "none" }}
              >
                <div className="viewButton">
                  <RemoveRedEyeOutlinedIcon />
                </div>
              </Link>
            </div>
            {params.row.paymentStatus === "UNPAID" &&
              params.row.is_suitability_accepted == 1 && (
                <div className="cellAction">
                  <Link
                    to={`/investment/edit/${params.row.id}/${params.row.campaign_detail.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <div className="editButton">
                      <EditOutlinedIcon />
                    </div>
                  </Link>
                </div>
              )}
            {params.row.is_suitability_accepted == 0 && (
              <div className="cellAction">
                <Link
                  to={`/suitability-overview/${params.row.id}`}
                  style={{ textDecoration: "none" }}
                >
                  <div className="editButton">
                    <PendingActionsIcon />
                  </div>
                </Link>
              </div>
            )}
          </>
        );
      },
    },
  ];

  return (
    <PageShell><PageHead title="Investments" /><div className="datatable">
      <Loader isLoading={loading} />
      <div className="datatableTitle">
        <div className="tablesearch">
          <input
            type="text"
            placeholder="Search Campaign..."
            onChange={handleQuery}
          />
          <SearchOutlinedIcon className="sarchicon" />
        </div>
        <h5 style={{ marginLeft: "-10%" }}>Investment</h5>
        {/* ACH generation moved to the orchestrated /ach-files page — the
            browser no longer talks to the ACH service or sees account
            numbers; the API builds files server-side with PAD/eligibility
            checks. */}
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/ach-files"
        >
          ACH files
        </Button>
      </div>
      <DataGrid
        className="datagrid"
        autoHeight
        rows={pageState.data}
        rowCount={pageState.total}
        rowsPerPageOptions={[10, 30, 50, 70, 100]}
        pagination
        page={pageState.page - 1}
        pageSize={pageState.pageSize}
        paginationMode="server"
        onPageChange={(newPage) => {
          setPageState((old) => ({ ...old, page: newPage + 1 }));
        }}
        onPageSizeChange={(newPageSize) =>
          setPageState((old) => ({ ...old, pageSize: newPageSize }))
        }
        columns={userColumns.concat(actionColumn)}
        columnVisibilityModel={columnVisibilityModel}
      />
    </div></PageShell>
  );
};

export default InvestmentList;
